import { Check, ChevronDown } from 'lucide-react';
import { ContextMenu as CM, DropdownMenu as DM, Select as S, Tooltip as T } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../cn';

const popover =
  'z-50 min-w-44 overflow-hidden rounded-lg border border-line bg-surface p-1 shadow-pop animate-pop-in';
const item =
  'relative flex h-9 cursor-default items-center gap-2.5 rounded-sm px-2.5 text-base text-ink outline-none select-none ' +
  'data-[highlighted]:bg-rail data-[disabled]:text-ink-4 [&_svg]:size-4 [&_svg]:text-ink-3';

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/** Dropdown menu of actions. Pass `null` entries to render separators. */
export function Menu({
  trigger,
  items,
  align = 'end',
}: {
  trigger: ReactNode;
  items: (MenuItem | null)[];
  align?: 'start' | 'end';
}) {
  return (
    <DM.Root>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content align={align} sideOffset={6} className={popover}>
          {items.map((it, i) =>
            it === null ? (
              <DM.Separator key={`s${i}`} className="my-1 h-px bg-line" />
            ) : (
              <DM.Item
                key={it.label}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(item, it.danger && 'text-danger [&_svg]:text-danger')}
              >
                {it.icon}
                <span className="flex-1">{it.label}</span>
                {it.shortcut && <span className="text-xs text-ink-3 tnum">{it.shortcut}</span>}
              </DM.Item>
            ),
          )}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

/**
 * Right-click (or long-press) actions on a row. Same items as `Menu`, reached
 * from the thing being acted on rather than from a separate trigger — useful on
 * dense tables where a per-row button would add a column of noise.
 */
export function ContextMenu({ items, children }: { items: (MenuItem | null)[]; children: ReactNode }) {
  return (
    <CM.Root>
      <CM.Trigger asChild>{children}</CM.Trigger>
      <CM.Portal>
        <CM.Content className={popover}>
          {items.map((it, i) =>
            it === null ? (
              <CM.Separator key={`s${i}`} className="my-1 h-px bg-line" />
            ) : (
              <CM.Item
                key={it.label}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(item, it.danger && 'text-danger [&_svg]:text-danger')}
              >
                {it.icon}
                <span className="flex-1">{it.label}</span>
                {it.shortcut && <span className="text-xs text-ink-3 tnum">{it.shortcut}</span>}
              </CM.Item>
            ),
          )}
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder = 'Выберите',
  id,
  size = 'md',
  disabled,
  className,
  ...aria
}: {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  'aria-label'?: string;
}) {
  const h =
    size === 'sm'
      ? 'h-8 text-sm px-2.5'
      : size === 'lg'
        ? 'h-12 text-md px-3.5'
        : 'h-10 text-base px-3';
  return (
    <S.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <S.Trigger
        id={id}
        {...aria}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-md border border-line-strong bg-surface text-left text-ink',
          'transition-[border-color,box-shadow] hover:border-ink-4 focus:border-primary focus:outline-none focus:ring-3 focus:ring-primary/15',
          'disabled:bg-sunken disabled:text-ink-3 data-[placeholder]:text-ink-3 aria-invalid:border-danger',
          h,
          className,
        )}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-4 text-ink-3" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className={cn(popover, 'max-h-80 w-[var(--radix-select-trigger-width)]')}
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item key={o.value} value={o.value} className={cn(item, 'pr-8')}>
                <S.ItemText>{o.label}</S.ItemText>
                {o.hint && <span className="ml-auto text-sm text-ink-3">{o.hint}</span>}
                <S.ItemIndicator className="absolute right-2.5">
                  <Check className="text-primary!" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <T.Provider delayDuration={400}>
      <T.Root>
        <T.Trigger asChild>{children}</T.Trigger>
        <T.Portal>
          <T.Content
            sideOffset={6}
            className="z-50 rounded-sm bg-ink px-2.5 py-1.5 text-sm text-white shadow-pop animate-fade-in"
          >
            {content}
          </T.Content>
        </T.Portal>
      </T.Root>
    </T.Provider>
  );
}
